import React, { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:4001/api";

//  Colour helpers 
const TEMP_COLORS   = { hot: "#ef4444", warm: "#f59e0b", cold: "#3b82f6" };
const STAGE_COLORS  = {
  visited:           "#64748b",
  interested:        "#3b82f6",
  proposal_sent:     "#f97316",
  mou_shared:        "#8b5cf6",
  mou_signed:        "#6366f1",
  payment_requested: "#d97706",
  closed_won:        "#16a34a",
  closed_lost:       "#dc2626",
};
const PRODUCT_COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#3b82f6","#8b5cf6"];
const INST_COLORS    = ["#3b82f6","#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#84cc16"];
const CITY_COLOR     = "#6366f1";

const STAGE_LABELS = {
  visited:           "Visited",
  interested:        "Interested",
  proposal_sent:     "Proposal Sent",
  mou_shared:        "MOU Shared",
  mou_signed:        "MOU Signed",
  payment_requested: "Payment Requested",
  closed_won:        "Closed  Won",
  closed_lost:       "Closed  Lost",
};

//  Value formatter 
function fmtVal(v) {
  if (!v && v !== 0) return "";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

//  Date helpers 
function todayStr() {
  return new Date().toISOString().split("T")[0];
}
function daysAgo(d) {
  const dt = new Date();
  dt.setDate(dt.getDate() - d);
  return dt.toISOString().split("T")[0];
}
function monthStart() {
  const dt = new Date();
  dt.setDate(1);
  return dt.toISOString().split("T")[0];
}

//  Period presets 
const PRESETS = [
  { label: "7 Days",    from: () => daysAgo(7),   to: todayStr },
  { label: "30 Days",   from: () => daysAgo(30),  to: todayStr },
  { label: "90 Days",   from: () => daysAgo(90),  to: todayStr },
  { label: "This Month",from: monthStart,          to: todayStr },
  { label: "Custom",    from: null,                to: null     },
];

// 
// SVG CHART PRIMITIVES
// 

/** Donut chart  data = [{label, value, color}] */
function DonutChart({ data = [], size = 180, title, subtitle }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  if (!total) return <EmptyChart label={title} />;

  const cx = size / 2, cy = size / 2, r = size * 0.35, stroke = size * 0.13;
  let cursor = -Math.PI / 2;
  const arcs = data
    .filter(d => d.value > 0)
    .map(d => {
      const angle = (d.value / total) * 2 * Math.PI;
      const x1 = cx + r * Math.cos(cursor);
      const y1 = cy + r * Math.sin(cursor);
      cursor += angle;
      const x2 = cx + r * Math.cos(cursor);
      const y2 = cy + r * Math.sin(cursor);
      return { ...d, angle, x1, y1, x2, y2, large: angle > Math.PI ? 1 : 0 };
    });

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <svg width={size} height={size}>
        {arcs.map((a, i) => (
          <path
            key={i}
            d={`M ${a.x1} ${a.y1} A ${r} ${r} 0 ${a.large} 1 ${a.x2} ${a.y2}`}
            fill="none"
            stroke={a.color}
            strokeWidth={stroke}
            strokeLinecap="butt"
          />
        ))}
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize={size * 0.11} fontWeight="700" fill="#1e293b">{total}</text>
        {subtitle && <text x={cx} y={cy + 10} textAnchor="middle" fontSize={size * 0.07} fill="#64748b">{subtitle}</text>}
        {title && <text x={cx} y={cy + 24} textAnchor="middle" fontSize={size * 0.065} fill="#94a3b8">{title}</text>}
      </svg>
      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", justifyContent: "center", maxWidth: size + 60 }}>
        {arcs.map((a, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
            <span style={{ color: "#64748b" }}>{a.label}</span>
            <span style={{ color: "#1e293b", fontWeight: 600 }}>{((a.value / total) * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Vertical bar chart */
function BarChart({ data = [], height = 180, color = "#6366f1", labelKey = "label", valueKey = "value", valueFormatter = v => v, title }) {
  if (!data.length) return <EmptyChart label={title} />;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const barW = Math.max(20, Math.min(48, Math.floor(560 / data.length) - 8));

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: height + 40, paddingBottom: 32, minWidth: data.length * (barW + 8) }}>
        {data.map((d, i) => {
          const h = Math.max(2, ((d[valueKey] || 0) / maxVal) * height);
          return (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexShrink: 0, width: barW }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#1e293b" }}>{valueFormatter(d[valueKey] || 0)}</span>
              <div
                style={{ width: "100%", height: h, background: Array.isArray(color) ? color[i % color.length] : color, borderRadius: "4px 4px 0 0", transition: "height 0.4s ease" }}
                title={`${d[labelKey]}: ${valueFormatter(d[valueKey] || 0)}`}
              />
              <span style={{ fontSize: 10, color: "#64748b", textAlign: "center", lineHeight: 1.2, maxWidth: barW, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {d[labelKey]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

//  Attendance breakdown colours 
const ATTEND_SEGMENTS = [
  { key: "field_days",  label: "Field",     color: "#10b981", icon: "" },
  { key: "exam_days",   label: "Exam Duty", color: "#f59e0b", icon: "" },
  { key: "office_days", label: "Office",    color: "#3b82f6", icon: "" },
  { key: "wfh_days",    label: "WFH",       color: "#8b5cf6", icon: "" },
  { key: "leave_days",  label: "Leave",     color: "#ef4444", icon: "" },
];

/** Stacked horizontal bar chart  one row per employee */
function AttendanceChart({ data = [] }) {
  if (!data.length) return <EmptyChart label="No attendance data" />;

  // max total days across all employees (for proportional row widths)
  const maxTotal = Math.max(
    ...data.map(d =>
      ATTEND_SEGMENTS.reduce((s, seg) => s + parseInt(d[seg.key] || 0), 0)
    ),
    1
  );

  return (
    <div>
      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", marginBottom: 14 }}>
        {ATTEND_SEGMENTS.map(seg => (
          <div key={seg.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: seg.color, flexShrink: 0 }} />
            <span style={{ color: "#64748b" }}>{seg.icon} {seg.label}</span>
          </div>
        ))}
      </div>

      {/* Rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.map((emp, i) => {
          const total = ATTEND_SEGMENTS.reduce((s, seg) => s + parseInt(emp[seg.key] || 0), 0);
          const rowPct = (total / maxTotal) * 100;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Name */}
              <div style={{
                width: 110, flexShrink: 0,
                fontSize: 11, fontWeight: 600, color: "#1e293b",
                textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={emp.name}>{emp.name}</div>

              {/* Bar wrapper */}
              <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 22, overflow: "hidden" }}>
                <div style={{ display: "flex", width: `${rowPct}%`, height: "100%", transition: "width 0.5s ease" }}>
                  {ATTEND_SEGMENTS.map(seg => {
                    const days = parseInt(emp[seg.key] || 0);
                    if (!days) return null;
                    const segPct = (days / total) * 100;
                    return (
                      <div
                        key={seg.key}
                        title={`${seg.label}: ${days} day${days !== 1 ? "s" : ""}`}
                        style={{
                          width: `${segPct}%`,
                          background: seg.color,
                          height: "100%",
                          transition: "width 0.4s ease",
                          cursor: "default",
                        }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Totals + breakdown */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", width: 28, textAlign: "right" }}>{total}d</span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 200 }}>
                  {ATTEND_SEGMENTS.map(seg => {
                    const days = parseInt(emp[seg.key] || 0);
                    if (!days) return null;
                    return (
                      <span key={seg.key} style={{
                        fontSize: 10, padding: "1px 6px", borderRadius: 10,
                        background: seg.color + "22", color: seg.color,
                        fontWeight: 700, whiteSpace: "nowrap",
                      }}>
                        {seg.icon}{days}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Team totals summary */}
      {data.length > 1 && (() => {
        const totals = ATTEND_SEGMENTS.map(seg => ({
          ...seg,
          total: data.reduce((s, d) => s + parseInt(d[seg.key] || 0), 0),
        }));
        const grandTotal = totals.reduce((s, t) => s + t.total, 0);
        return (
          <div style={{ marginTop: 16, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Team Total  {grandTotal} days across {data.length} employees
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {totals.map(t => (
                <div key={t.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: t.color }}>{t.total}</div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>{t.icon} {t.label}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>
                    {grandTotal > 0 ? Math.round((t.total / grandTotal) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** Horizontal bar chart */
function HBarChart({ data = [], color = "#6366f1", valueFormatter = v => v }) {
  if (!data.length) return <EmptyChart label="No data" />;
  const maxVal = Math.max(...data.map(d => d.value || 0), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {data.map((d, i) => {
        const pct = ((d.value || 0) / maxVal) * 100;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: "#64748b", width: 120, textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.label}>{d.label}</span>
            <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 18, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: Array.isArray(color) ? color[i % color.length] : color, borderRadius: 4, transition: "width 0.4s ease" }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", width: 52, flexShrink: 0 }}>{valueFormatter(d.value || 0)}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Area / line chart for trend */
function AreaChart({ data = [], height = 140, color = "#6366f1", labelKey = "month", valueKey = "visits" }) {
  if (!data.length) return <EmptyChart label="No trend data" />;
  const W = 520, H = height;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const pts = data.map((d, i) => ({
    x: 40 + (i / Math.max(data.length - 1, 1)) * (W - 60),
    y: H - 20 - ((d[valueKey] || 0) / maxVal) * (H - 36),
    label: d[labelKey],
    val: d[valueKey] || 0,
  }));
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const area = `${line} L ${pts[pts.length - 1].x} ${H - 20} L ${pts[0].x} ${H - 20} Z`;

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map(f => {
          const y = H - 20 - f * (H - 36);
          return (
            <g key={f}>
              <line x1={40} y1={y} x2={W - 20} y2={y} stroke="#e2e8f0" strokeWidth={1} />
              <text x={36} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">{Math.round(f * maxVal)}</text>
            </g>
          );
        })}
        {/* Area fill */}
        <path d={area} fill={color} fillOpacity={0.12} />
        {/* Line */}
        <path d={line} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" />
        {/* Points + labels */}
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />
            <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize={9} fontWeight="700" fill={color}>{p.val}</text>
            <text x={p.x} y={H - 6} textAnchor="middle" fontSize={9} fill="#64748b">{p.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/** Stat KPI card */
function KPI({ label, value, sub, color = "#6366f1", icon }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: `4px solid ${color}`, minWidth: 140 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: "#1e293b" }}>{value ?? ""}</span>
        {icon && <span style={{ fontSize: 24 }}>{icon}</span>}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 16px", color: "#94a3b8", fontSize: 13 }}>
       {label || "No data available"}
    </div>
  );
}

function SectionCard({ title, children, span = 1 }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", gridColumn: `span ${span}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 14, borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

// 
// MAIN COMPONENT
// 

export default function AnalyticsDashboard({ accent = "#6366f1", userRole, userCompany }) {
  const [preset,    setPreset]    = useState(1);   // index into PRESETS (default 30 days)
  const [customFrom,setCustomFrom]= useState(daysAgo(30));
  const [customTo,  setCustomTo]  = useState(todayStr());
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  // Drill-down: click a company bar  filter employees to that company
  const [drillCompany, setDrillCompany] = useState(null);

  const isCustom = preset === PRESETS.length - 1;
  const canSeeAllCompanies = ["superadmin","executive"].includes(userRole);

  const getRange = useCallback(() => {
    if (isCustom) return { from: customFrom, to: customTo };
    const p = PRESETS[preset];
    return { from: p.from(), to: p.to() };
  }, [preset, isCustom, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { from, to } = getRange();
      const token = localStorage.getItem("crm_token");
      const params = new URLSearchParams({ from, to });
      if (drillCompany) params.set("company", drillCompany);
      const res = await fetch(`${API}/reports/analytics?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getRange, drillCompany]);

  useEffect(() => { load(); }, [load]);

  //  helpers 
  const tempData = data
    ? [
        { label: "Hot",  value: data.temp_distribution?.hot  || 0, color: TEMP_COLORS.hot  },
        { label: "Warm", value: data.temp_distribution?.warm || 0, color: TEMP_COLORS.warm },
        { label: "Cold", value: data.temp_distribution?.cold || 0, color: TEMP_COLORS.cold },
      ]
    : [];

  const winLossData = data
    ? [
        { label: "Won",  value: parseInt(data.win_rate?.won    || 0), color: "#16a34a" },
        { label: "Lost", value: parseInt(data.win_rate?.lost   || 0), color: "#dc2626" },
        { label: "Open", value: parseInt(data.win_rate?.active || 0), color: "#94a3b8" },
      ]
    : [];

  const productData = (data?.product_interest || []).map((p, i) => ({
    label: (p.product || p.interested_in || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    value: parseInt(p.count || 0),
    color: PRODUCT_COLORS[i % PRODUCT_COLORS.length],
  }));

  const instData = (data?.institution_types || []).map((p, i) => ({
    label: (p.institution_type || "Other").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    value: parseInt(p.count || 0),
    color: INST_COLORS[i % INST_COLORS.length],
  }));

  const cityData = (data?.city_wise || []).map(p => ({
    label: p.city || "Unknown",
    value: parseInt(p.visit_count || p.count || 0),
  }));

  const empVisitData = (data?.emp_visits || []).map(p => ({
    label: (p.name || "").split(" ")[0],
    value: parseInt(p.visit_count || 0),
  }));

  const followupData = (data?.followup_rates || [])
    .filter(p => parseInt(p.total || 0) > 0)
    .map(p => {
      const total = parseInt(p.total || 0);
      const done  = parseInt(p.completed || 0);
      return {
        label: (p.name || "").split(" ")[0],
        value: total > 0 ? Math.round((done / total) * 100) : 0,
      };
    });

  const pipelineData = (Array.isArray(data?.pipeline_dist)
    ? data.pipeline_dist
    : Object.entries(data?.pipeline_dist || {}).map(([pipeline_stage, count]) => ({ pipeline_stage, count }))
  ).map(p => ({
    label: STAGE_LABELS[p.pipeline_stage] || p.pipeline_stage,
    value: parseInt(p.count || 0),
    color: STAGE_COLORS[p.pipeline_stage] || "#94a3b8",
  }));

  const attendanceData = (data?.attendance_breakdown || []).map(emp => ({
    name:        emp.name,
    company:     emp.company,
    field_days:  parseInt(emp.field_days  || 0),
    exam_days:   parseInt(emp.exam_days   || 0),
    office_days: parseInt(emp.office_days || 0),
    wfh_days:    parseInt(emp.wfh_days    || 0),
    leave_days:  parseInt(emp.leave_days  || 0),
  }));

  const compData = (data?.company_comparison || []).map(p => ({
    label:  (p.company || "").toUpperCase(),
    visits: parseInt(p.visits || p.total_visits || 0),
    leads:  parseInt(p.active_leads || 0),
    won:    parseInt(p.won || 0),
  }));

  const monthlyData = (data?.monthly_trend || []).map(p => ({
    month:  p.month,
    visits: parseInt(p.visit_count || p.visits || 0),
    leads:  parseInt(p.new_leads || 0),
  }));

  // Derived KPIs
  const totalVisits    = monthlyData.reduce((s, m) => s + m.visits, 0);
  const activeLeads    = pipelineData.filter(p => !["Closed  Won","Closed  Lost"].includes(p.label)).reduce((s, p) => s + p.value, 0);
  const wonLeads       = parseInt(data?.win_rate?.won  || 0);
  const lostLeads      = parseInt(data?.win_rate?.lost || 0);
  const winRate        = (wonLeads + lostLeads) > 0 ? Math.round((wonLeads / (wonLeads + lostLeads)) * 100) : 0;
  // Pipeline value: sum deal_value for active leads from pipeline_dist
  const pipelineValNum = (Array.isArray(data?.pipeline_dist) ? data.pipeline_dist : [])
    .filter(p => !["closed_won","closed_lost"].includes(p.pipeline_stage))
    .reduce((s, p) => s + parseFloat(p.total_value || 0), 0);
  const pipelineVal    = fmtVal(pipelineValNum);
  const totalEmployees = new Set((data?.emp_visits || []).map(e => e.user_id)).size;

  //  render 
  return (
    <div style={{ padding: "0 0 24px 0" }}>

      {/* Period selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        {PRESETS.map((p, i) => (
          <button
            key={i}
            onClick={() => setPreset(i)}
            style={{
              padding: "6px 14px", borderRadius: 20, border: "1.5px solid",
              borderColor: preset === i ? accent : "#e2e8f0",
              background: preset === i ? accent : "#fff",
              color: preset === i ? "#fff" : "#64748b",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >{p.label}</button>
        ))}
        {isCustom && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0" }} />
            <span style={{ color: "#94a3b8", fontSize: 12 }}>to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0" }} />
            <button onClick={load} style={{ padding: "6px 16px", borderRadius: 20, background: accent, color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Apply</button>
          </>
        )}
        {drillCompany && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, background: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: 12, fontWeight: 600 }}>
              Filtered: {drillCompany.toUpperCase()}
            </span>
            <button onClick={() => setDrillCompany(null)} style={{ fontSize: 11, color: "#64748b", background: "#f1f5f9", border: "none", borderRadius: 8, padding: "4px 10px", cursor: "pointer" }}>
               Clear
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#dc2626", padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8", fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}></div>
          Loading analytics
        </div>
      ) : (
        <>
          {/*  KPI Row  */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
            <KPI label="Total Visits"   value={totalVisits}   color="#6366f1" icon="" sub="in period" />
            <KPI label="Active Leads"   value={activeLeads}   color="#3b82f6" icon="" sub="in pipeline" />
            <KPI label="Win Rate"       value={`${winRate}%`} color="#16a34a" icon="" sub={`${wonLeads} won`} />
            <KPI label="Pipeline Value" value={pipelineVal}   color="#d97706" icon="" sub="open deals" />
            <KPI label="Closed Won"     value={wonLeads}      color="#16a34a" icon="" />
            <KPI label="Closed Lost"    value={lostLeads}     color="#dc2626" icon="" />
            {totalEmployees > 0 && <KPI label="Active Reps"   value={totalEmployees} color={accent} icon="" sub="with visits" />}
          </div>

          {/*  Main grid  */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>

            {/* Monthly Trend  full width */}
            <SectionCard title=" Monthly Visit Trend (Last 6 Months)" span={2}>
              <AreaChart data={monthlyData} color={accent} labelKey="month" valueKey="visits" />
            </SectionCard>

            {/* Temperature distribution */}
            <SectionCard title=" Lead Temperature Distribution">
              <DonutChart data={tempData} title="visits" subtitle="Total" size={170} />
            </SectionCard>

            {/* Win / Loss ratio */}
            <SectionCard title=" Win / Loss Ratio">
              <DonutChart data={winLossData} title="closed" subtitle="Leads" size={170} />
            </SectionCard>

            {/* Product Interest */}
            <SectionCard title=" Product Interest">
              <DonutChart data={productData} title="visits" subtitle="Interest" size={170} />
            </SectionCard>

            {/* Institution Types */}
            <SectionCard title=" Institution Types">
              <DonutChart data={instData} title="visits" subtitle="Types" size={170} />
            </SectionCard>

            {/* Visits by Employee  full width */}
            <SectionCard title=" Visits by Employee" span={2}>
              {empVisitData.length ? (
                <BarChart data={empVisitData} color={PRODUCT_COLORS} labelKey="label" valueKey="value" height={160} />
              ) : <EmptyChart label="No visit data" />}
            </SectionCard>

            {/* City-wise */}
            <SectionCard title=" Top Cities by Visits" span={2}>
              {cityData.length ? (
                <HBarChart data={cityData} color={CITY_COLOR} />
              ) : <EmptyChart label="No city data" />}
            </SectionCard>

            {/* Pipeline Distribution */}
            <SectionCard title=" Pipeline Stage Distribution" span={2}>
              {pipelineData.length ? (
                <BarChart data={pipelineData} color={pipelineData.map(p => p.color)} labelKey="label" valueKey="value" height={140} />
              ) : <EmptyChart label="No pipeline data" />}
            </SectionCard>

            {/* Follow-up completion rate by employee */}
            <SectionCard title=" Follow-up Completion Rate (%)" span={2}>
              {followupData.length ? (
                <HBarChart data={followupData} color={accent} valueFormatter={v => `${parseFloat(v).toFixed(0)}%`} />
              ) : <EmptyChart label="No follow-up data" />}
            </SectionCard>

            {/* Attendance Breakdown  full width */}
            <SectionCard title=" Team Attendance Breakdown" span={2}>
              <AttendanceChart data={attendanceData} />
            </SectionCard>

            {/* Company Comparison (superadmin / executive only) */}
            {canSeeAllCompanies && compData.length > 0 && (
              <SectionCard title=" Company Comparison  click a bar to drill down" span={2}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                  {/* Visits comparison */}
                  <div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, fontWeight: 600 }}>Total Visits</div>
                    <div style={{ display: "flex", gap: 16, alignItems: "flex-end", height: 120 }}>
                      {compData.map((c, i) => {
                        const maxV = Math.max(...compData.map(x => x.visits), 1);
                        const h = Math.max(4, (c.visits / maxV) * 100);
                        const clr = c.label === "EBEK" ? "#E31E24" : "#2AB5A5";
                        return (
                          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1, cursor: "pointer" }}
                            onClick={() => setDrillCompany(drillCompany === c.label.toLowerCase() ? null : c.label.toLowerCase())}
                          >
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{c.visits}</span>
                            <div style={{ width: "70%", height: h, background: clr, borderRadius: "4px 4px 0 0", opacity: drillCompany && drillCompany !== c.label.toLowerCase() ? 0.35 : 1, transition: "all 0.3s" }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: clr }}>{c.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Leads + Won comparison */}
                  <div>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, fontWeight: 600 }}>Active Leads & Closed Won</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {compData.map((c, i) => {
                        const clr = c.label === "EBEK" ? "#E31E24" : "#2AB5A5";
                        return (
                          <div key={i} style={{ cursor: "pointer" }} onClick={() => setDrillCompany(drillCompany === c.label.toLowerCase() ? null : c.label.toLowerCase())}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: clr }}>{c.label}</span>
                              <span style={{ fontSize: 11, color: "#64748b" }}>Leads: {c.leads}  Won: {c.won}</span>
                            </div>
                            <div style={{ background: "#f1f5f9", borderRadius: 4, height: 10, overflow: "hidden" }}>
                              <div style={{ width: `${Math.min(100, (c.leads / Math.max(...compData.map(x => x.leads), 1)) * 100)}%`, height: "100%", background: clr, borderRadius: 4 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {drillCompany && (
                  <div style={{ marginTop: 12, padding: "8px 12px", background: "#fef9c3", borderRadius: 8, fontSize: 12, color: "#92400e" }}>
                     Showing employee breakdown for <strong>{drillCompany.toUpperCase()}</strong>. The charts above now reflect only this company. Click the bar again or "Clear" to reset.
                  </div>
                )}
              </SectionCard>
            )}

          </div>
        </>
      )}
    </div>
  );
}
